/**
 * The tick lattice — §2 and §3 of contracts/cue-model.ts.
 *
 * 120 000 ticks = 1 second. Every launch framerate divides it exactly, so a MicroDVD frame
 * index and a millisecond timestamp are two exact views of one integer and there is nothing
 * to convert until a writer serialises.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE: the float 23.976 never appears. Framerates are exact
 * rationals `{num, den}` and every conversion is integer arithmetic with one explicit
 * rounding step at the end.
 */

import type {
  FrameCount,
  Framerate,
  Rounding,
  TickDelta,
  Ticks,
} from '../../../contracts/cue-model.ts';
import { TICKS_PER_MS, TICKS_PER_SECOND } from '../../../contracts/cue-model.ts';

export { TICKS_PER_MS, TICKS_PER_SECOND };

/** Brand casts. Confined to this file so nothing else has to reach for `as unknown as`. */
export const T = {
  ticks: (n: number): Ticks => n as Ticks,
  delta: (n: number): TickDelta => n as TickDelta,
  frames: (n: number): FrameCount => n as FrameCount,
};

/*───────────────────────────────────────────────────────────────────────────────────────
  Rational helpers. All integer, all exact.
───────────────────────────────────────────────────────────────────────────────────────*/

export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x === 0 ? 1 : x;
}

/** True for a framerate we can do exact arithmetic with at all. den 0 is an invalid-op case. */
export function isValidFramerate(fps: Framerate | null | undefined): fps is Framerate {
  return (
    !!fps &&
    Number.isFinite(fps.num) &&
    Number.isFinite(fps.den) &&
    Number.isInteger(fps.num) &&
    Number.isInteger(fps.den) &&
    fps.num > 0 &&
    fps.den > 0
  );
}

/**
 * Exact ticks per frame as a reduced rational — never assume it is an integer for an
 * arbitrary rate. 23.976 (24000/1001) → 5005/1. 29.97 → 4004/1. 25 → 4800/1.
 */
export function ticksPerFrameExact(fps: Framerate): { readonly num: number; readonly den: number } {
  assertFramerate(fps);
  const num = TICKS_PER_SECOND * fps.den;
  const den = fps.num;
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

/** True when ticks↔frames is lossless. Every launch rate is. */
export function isLatticeAligned(fps: Framerate): boolean {
  return ticksPerFrameExact(fps).den === 1;
}

/**
 * Integer ticks per frame. Callers MUST have checked isLatticeAligned, or accept the
 * rounding documented here: half-up on the exact rational.
 */
export function ticksPerFrame(fps: Framerate): number {
  const { num, den } = ticksPerFrameExact(fps);
  return den === 1 ? num : roundWith(num / den, 'half-up');
}

function assertFramerate(fps: Framerate | null | undefined): asserts fps is Framerate {
  if (!isValidFramerate(fps)) {
    throw new RangeError(
      `framerate must be an exact rational with num > 0 and den > 0; got ${JSON.stringify(fps)}`,
    );
  }
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Rounding. Named explicitly because "round" silently means four different things.
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * The single rounding implementation. `half-up` is ties AWAY FROM ZERO (so −0.5 → −1),
 * which is the convention the transcript wire states and the one every subtitle format
 * assumes. `half-even` is banker's rounding.
 */
/**
 * Normalise negative zero away. `Math.ceil(-0.5)` is `-0`, and a `-0` reaching a Ticks value
 * serialises as `-0` in JSON — which contracts/transcript-wire.md W12 bans outright.
 * Every rounding exit in this file goes through here.
 */
const nz = (v: number): number => (v === 0 ? 0 : v);

export function roundWith(x: number, r: Rounding = 'half-up'): number {
  return nz(roundWithRaw(x, r));
}

function roundWithRaw(x: number, r: Rounding): number {
  switch (r) {
    case 'floor':
      return Math.floor(x);
    case 'ceil':
      return Math.ceil(x);
    case 'half-even': {
      const f = Math.floor(x);
      const diff = x - f;
      if (diff > 0.5) return f + 1;
      if (diff < 0.5) return f;
      return f % 2 === 0 ? f : f + 1;
    }
    case 'half-up': {
      // ties away from zero, without Math.round's asymmetry at negative halves
      return x < 0 ? -Math.round(-x) : Math.round(x);
    }
  }
}

/**
 * Exact rounded division of integers: round(a / b) under `r`, computed without ever
 * materialising a float that could lose the tie. `b` must be > 0.
 */
export function divRound(a: number, b: number, r: Rounding = 'half-up'): number {
  return nz(divRoundRaw(a, b, r));
}

function divRoundRaw(a: number, b: number, r: Rounding): number {
  if (b <= 0) throw new RangeError(`divRound: divisor must be > 0, got ${b}`);
  if (!Number.isSafeInteger(a) || !Number.isSafeInteger(b)) return roundWith(a / b, r);
  const q = Math.floor(a / b);
  const rem = a - q * b; // always in [0, b)
  if (rem === 0) return q;
  const twice = rem * 2;
  switch (r) {
    case 'floor':
      return q;
    case 'ceil':
      return q + 1;
    case 'half-even':
      if (twice > b) return q + 1;
      if (twice < b) return q;
      return q % 2 === 0 ? q : q + 1;
    case 'half-up':
      // ties away from zero: for a < 0 the floor-based remainder biases toward −∞,
      // so an exact half must round down (away from zero) rather than up.
      if (twice > b) return q + 1;
      if (twice < b) return q;
      return a < 0 ? q : q + 1;
  }
}

/*───────────────────────────────────────────────────────────────────────────────────────
  §2 arithmetic helpers. Every mutation in the library routes through these.
───────────────────────────────────────────────────────────────────────────────────────*/

export function msToTicks(ms: number): Ticks {
  return T.ticks(
    Number.isInteger(ms) ? ms * TICKS_PER_MS : roundWith(ms * TICKS_PER_MS, 'half-up'),
  );
}

export function ticksToMs(t: Ticks, r: Rounding = 'half-up'): number {
  return divRound(t as number, TICKS_PER_MS, r);
}

/** Seconds → ticks, half-up. The one conversion contracts/transcript-wire.md §3 specifies. */
export function secondsToTicks(s: number): Ticks {
  return T.ticks(roundWith(s * TICKS_PER_SECOND, 'half-up'));
}

export function addTicks(t: Ticks, d: TickDelta): Ticks {
  return T.ticks((t as number) + (d as number));
}

export function subTicks(a: Ticks, b: Ticks): TickDelta {
  return T.delta((a as number) - (b as number));
}

export function scaleTicks(
  t: Ticks,
  ratio: { num: number; den: number },
  r: Rounding = 'half-up',
): Ticks {
  if (!Number.isFinite(ratio.num) || !Number.isFinite(ratio.den) || ratio.den === 0) {
    throw new RangeError(`scaleTicks: invalid ratio ${JSON.stringify(ratio)}`);
  }
  // Normalise the sign onto the numerator so divRound's positive-divisor rule holds.
  const num = ratio.den < 0 ? -ratio.num : ratio.num;
  const den = Math.abs(ratio.den);
  return T.ticks(divRound((t as number) * num, den, r));
}

export function framesToTicks(f: FrameCount, fps: Framerate): Ticks {
  const tpf = ticksPerFrameExact(fps);
  return T.ticks(divRound((f as number) * tpf.num, tpf.den, 'half-up'));
}

export function ticksToFrames(t: Ticks, fps: Framerate, r: Rounding = 'half-up'): FrameCount {
  const tpf = ticksPerFrameExact(fps);
  // frames = t / (tpf.num / tpf.den) = t * tpf.den / tpf.num
  return T.frames(divRound((t as number) * tpf.den, tpf.num, r));
}

/** Snap to a quantum expressed in ticks (ms=120, cs=1200, ds=12000, frame=ticksPerFrame). */
export function quantizeTicks(t: Ticks, quantumTicks: number, r: Rounding = 'half-up'): Ticks {
  if (!Number.isFinite(quantumTicks) || quantumTicks <= 0) {
    throw new RangeError(`quantizeTicks: quantum must be > 0, got ${quantumTicks}`);
  }
  return T.ticks(divRound(t as number, quantumTicks, r) * quantumTicks);
}

/** True when `t` sits exactly on the frame lattice of `fps` — the off-lattice-rule predicate. */
export function isOnFrameLattice(t: Ticks, fps: Framerate): boolean {
  const tpf = ticksPerFrameExact(fps);
  // t is on the lattice iff t * den is divisible by num
  const scaled = (t as number) * tpf.den;
  return scaled % tpf.num === 0;
}

/**
 * THE karaoke / word-timing serialisation primitive (§2).
 *
 * Returns successive quantized DURATIONS in units of the quantum, using cumulative carry:
 * `round((b − anchor)/q) − prevRounded`. Never rounds each duration independently.
 *
 * GUARANTEE: Σ result === round((boundaries.at(-1) − anchor) / q) exactly, forever.
 * Naive per-syllable rounding drifts hundreds of ms across a 400-syllable song and the
 * karaoke visibly runs ahead of the vocal.
 */
export function cumulativeQuantize(
  anchor: Ticks,
  boundaries: readonly Ticks[],
  quantumTicks: number,
  r: Rounding = 'half-up',
): readonly number[] {
  if (!Number.isFinite(quantumTicks) || quantumTicks <= 0) {
    throw new RangeError(`cumulativeQuantize: quantum must be > 0, got ${quantumTicks}`);
  }
  const out: number[] = [];
  let prev = 0;
  for (const b of boundaries) {
    const cum = divRound((b as number) - (anchor as number), quantumTicks, r);
    out.push(cum - prev);
    prev = cum;
  }
  return out;
}
